import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cell", "heritageDisplay", "magicDisplay", "attributesPointsDisplay", "skillsPointsDisplay", "resourcesDisplay"]
  
  static values = {
    tableData: Array
  }

  connect() {
    this.tableData = [
      {
        category: 'Heritage',
        values: [
          { rank: 'A', display: 'Any' },
          { rank: 'B', display: 'Any' },
          { rank: 'C', display: 'Green/Blighted/Uplifted' },
          { rank: 'D', display: 'Synthetic' },
          { rank: 'E', display: 'Human' }
        ]
      },
      {
        category: 'Magic',
        values: [
          { rank: 'A', display: 'Archmage' },
          { rank: 'B', display: 'Mage' },
          { rank: 'C', display: 'Amp/Speaker' },
          { rank: 'D', display: 'Hedge' },
          { rank: 'E', display: 'Hedge' }
        ]
      },
      {
        category: 'Attributes',
        values: [
          { rank: 'A', display: 46 },
          { rank: 'B', display: 38 },
          { rank: 'C', display: 32 },
          { rank: 'D', display: 29 },
          { rank: 'E', display: 27 }
        ]
      },
      {
        category: 'Skills',
        values: [
          { rank: 'A', display: 48 },
          { rank: 'B', display: 36 },
          { rank: 'C', display: 30 },
          { rank: 'D', display: 27 },
          { rank: 'E', display: 24 }
        ]
      },
      {
        category: 'Resource',
        values: [
          { rank: 'A', display: 1200000 },
          { rank: 'B', display: 600000 },
          { rank: 'C', display: 250000 },
          { rank: 'D', display: 60000 },
          { rank: 'E', display: 25000 }
        ]
      }
    ]

    // Load saved values
    this.loadSavedValues()

    this.element.closest('form')?.addEventListener('submit', () => {
      this.syncHiddenFieldsFromSelections()
    })

    this.dispatch("connected")
  }

  loadSavedValues() {
    const form = this.element.closest('form')
    const savedValues = {
      heritage: form.querySelector('#character_priority_heritage').value,
      magic: form.querySelector('#character_priority_magic').value,
      attributes: form.querySelector('#character_priority_attributes').value,
      skills: form.querySelector('#character_priority_skills').value,
      resources: form.querySelector('#character_priority_resources').value
    }

    // For each saved value, find and select the matching cell
    this.cellTargets.forEach(cell => {
      const row = parseInt(cell.dataset.row)
      const rank = cell.dataset.rank
      const savedValue = this.normalizeSavedValue(row, savedValues)
      
      if (
        savedValue &&
        rank === savedValue
      ) {
        this.selectCell({ currentTarget: cell })
      }
    })
  }

  normalizeSavedValue(row, savedValues) {
    const valueByRow = [
      savedValues.heritage,
      savedValues.magic,
      savedValues.attributes,
      savedValues.skills,
      savedValues.resources
    ][row]

    if (!valueByRow) return null

    const normalized = valueByRow.trim()
    if (/^[A-E]$/.test(normalized)) return normalized

    const rowValues = this.tableData[row].values
    const matchedValue = rowValues.find((value) => String(value.display) === normalized)
    return matchedValue ? matchedValue.rank : null
  }

  selectCell(event) {
    const clickedCell = event.currentTarget
    const content = clickedCell.dataset.display || clickedCell.textContent.trim()
    const rank = clickedCell.dataset.rank

    const row = parseInt(clickedCell.dataset.row)
    const col = parseInt(clickedCell.dataset.col)
    
    // Clear previous selections in row and column
    this.cellTargets.forEach(cell => {
      if (cell.dataset.row === row.toString() || cell.dataset.col === col.toString()) {
        if (cell !== clickedCell && cell.classList.contains('bg-blue-500')) {
          this.clearSelection(cell)
        }
        this.applySelectedState(cell, false)
      }
    })
    
    this.applySelectedState(clickedCell, true)
    this.updateDisplay(row, content)
    this.updateHiddenFields(row, rank)
  }

  clearSelection(cell) {
    const row = parseInt(cell.dataset.row)
    const clearedContent = row <= 1 ? 'None' : '0'
    this.updateDisplay(row, clearedContent)
    this.updateHiddenFields(row, '')
  }

  syncHiddenFieldsFromSelections() {
    for (let row = 0; row <= 4; row++) {
      this.updateHiddenFields(row, '')
    }

    this.cellTargets.forEach(cell => {
      if (cell.classList.contains('bg-blue-500')) {
        const row = parseInt(cell.dataset.row)
        this.updateHiddenFields(row, cell.dataset.rank)
      }
    })
  }

  updateDisplay(row, content) {
    switch (row) {
      case 0:
        this.heritageDisplayTarget.textContent = content
        break
      case 1:
        this.magicDisplayTarget.textContent = content
        break
      case 2:
        this.attributesPointsDisplayTarget.textContent = content
        this.dispatch("attributesUpdated", { detail: { points: content } })
        break
      case 3:
        this.skillsPointsDisplayTarget.textContent = content
        this.dispatch("skillsUpdated", { detail: { points: content } })
        break
      case 4:
        const formattedNumber = parseInt(String(content).replace(/,/g, ''), 10).toLocaleString()
        this.resourcesDisplayTarget.textContent = formattedNumber
        break
    }
  }

  updateHiddenFields(row, rank) {
    const form = this.element.closest('form')
    switch (row) {
      case 0:
        form.querySelector('#character_priority_heritage').value = rank
        break
      case 1:
        form.querySelector('#character_priority_magic').value = rank
        break
      case 2:
        form.querySelector('#character_priority_attributes').value = rank
        break
      case 3:
        form.querySelector('#character_priority_skills').value = rank
        break
      case 4:
        form.querySelector('#character_priority_resources').value = rank
        break
    }
  }

  applySelectedState(cell, selected) {
    cell.classList.toggle('bg-blue-500', selected)
    cell.classList.toggle('text-white', selected)
    cell.classList.toggle('border-blue-500', selected)
    cell.classList.toggle('shadow-md', selected)
    cell.classList.toggle('dark:bg-blue-600', selected)
    cell.classList.toggle('dark:border-blue-500', selected)

    cell.classList.toggle('bg-gray-50/80', !selected)
    cell.classList.toggle('text-gray-500', !selected)
    cell.classList.toggle('border-gray-200', !selected)
    cell.classList.toggle('dark:bg-gray-900/40', !selected)
    cell.classList.toggle('dark:border-gray-700', !selected)

    cell.setAttribute('aria-pressed', selected ? 'true' : 'false')
  }
} 
